// File: src/pages/Events.jsx

import React, { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { EventsContext } from "../layouts/StandardLayout";



import MinimalFooter from "../components/MinimalFooter";

import EventCard from "../components/EventCard";
import EventsFiltersAndHeadline from "../components/EventsFiltersAndHeadline/EventsFiltersAndHeadline";

import SearchBarWithSheet from "../components/SearchBarWithSheet";

import EventLiveEntrySheet from "../components/EventLiveEntrySheet";
import SelectMarket from '../assets/Illustrations/SelectMarket.svg';
import NoEvent from "../assets/NoEvent.svg";
import NoSaved from "../assets/NoSaved.svg";
import { ReactComponent as TribeLogo } from "../assets/TribeLogo.svg";

import { EventAlreadyStartedBottomSheet } from "../components/NoRegistrationBottomSheet";





import CategoryPills from "../components/CategoryPills";

import CardRequiredReservationBanner from "../components/CardRequiredReservationBanner";
import NonLoggedInEventsLanding from "../components/NonLoggedInEventsLanding";

import { getEventStartMs, getEventEndMs } from "../utils/eventTime";

function isEventOngoing(event) {
  const startMs = getEventStartMs(event);
  const endMs = getEventEndMs(event);
  if (!startMs || !endMs) return false;
  const now = Date.now();
  return now >= startMs && now <= endMs;
}

function isEventNotStarted(event) {
  const startMs = getEventStartMs(event);
  if (!startMs) return false;
  return Date.now() < startMs;
}

function isEventEnded(event) {
  const endMs = getEventEndMs(event);
  if (!endMs) return false;
  return Date.now() > endMs;
}

function getCalendarDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getMinutesFromMidnight(ms, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(ms));

  const map = {};
  for (const p of parts) map[p.type] = p.value;

  return Number(map.hour) * 60 + Number(map.minute);
}

function normalizeRange(startMin, endMin) {
  if (endMin < startMin) endMin += 1440;
  return { startMin, endMin };
}

function hasAtLeast30MinOverlap(evtStart, evtEnd, slotStart, slotEnd) {
  const overlapStart = Math.max(evtStart, slotStart);
  const overlapEnd = Math.min(evtEnd, slotEnd);
  return overlapEnd - overlapStart >= 30;
}

function getEventTimeSlots(eventStartMs, eventEndMs, timeZone) {
  if (!eventStartMs || !eventEndMs) return [];

  const { startMin, endMin } = normalizeRange(
    getMinutesFromMidnight(eventStartMs, timeZone),
    getMinutesFromMidnight(eventEndMs, timeZone),
  );

  const slots = [];

  if (hasAtLeast30MinOverlap(startMin, endMin, 360, 720)) slots.push("morning");
  if (hasAtLeast30MinOverlap(startMin, endMin, 720, 1020))
    slots.push("afternoon");
  if (hasAtLeast30MinOverlap(startMin, endMin, 1020, 1320))
    slots.push("evening");
  if (hasAtLeast30MinOverlap(startMin, endMin, 1320, 1440))
    slots.push("latenight");

  // Handle events crossing midnight
  if (endMin > 1440) {
    const wrappedEnd = endMin - 1440;
    if (hasAtLeast30MinOverlap(0, wrappedEnd, 0, 360)) {
      slots.push("latenight");
    }
  }

  return Array.from(new Set(slots));
}

function shouldShowReservationStatus(membership) {
  if (!membership) {
    return false;
  }

  const accessStatus = String(
    membership.reservationAccessStatus || "",
  ).toLowerCase();

  const membershipStatus = String(
    membership.membershipStatus || "",
  ).toLowerCase();

  const remainingValue =
    membership.membershipReservationsRemaining;

  const remainingIsKnown =
    remainingValue !== null &&
    remainingValue !== undefined &&
    remainingValue !== "" &&
    Number.isFinite(Number(remainingValue));

  const hasNoReservationsRemaining =
    remainingIsKnown && Number(remainingValue) <= 0;

  const hasAttendanceWarning =
    accessStatus === "warning" ||
    membership.attendanceWarning === true ||
    (
      Number(membership.membershipNoShowStrikes || 0) === 1 &&
      membership.membershipCardRequired !== true
    );

  const hasCardOnFile = Boolean(
    membership.hasCardOnFile ||
      membership.membershipPaymentMethodId ||
      membership.membershipCardLast4,
  );

  const hasCardIssue =
    accessStatus === "card_issue" ||
    membership.membershipCardRequiresReplacement === true ||
    ["card_issue", "past_due", "unpaid"].includes(
      membershipStatus,
    );

  const needsCard =
    accessStatus === "card_required" ||
    (
      membership.membershipCardRequired === true &&
      !hasCardOnFile
    );

  const accessIsBlocked = [
    "limit_reached",
    "suspended",
    "removed",
    "paused",
    "blocked",
  ].includes(accessStatus);

return (
  remainingIsKnown ||
  hasAttendanceWarning ||
  hasCardIssue ||
  needsCard ||
  accessIsBlocked
);
}

export default function Events({
  membership = null,
  recommendedEvent = null,
  reservationIntroBlocking = false,
  onFilterOverlayChange,
}) {
  // normalize any value for text search
  function norm(v) {
    return (v ?? "").toString().toLowerCase().trim();
  }

  useEffect(() => {
    document.documentElement.classList.add("events-page");
    return () => {
      document.documentElement.classList.remove("events-page");
    };
  }, []);

  const location = useLocation();
  const navigate = useNavigate();

  function isTrueMobileDevice() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent.toLowerCase();
    return /android|iphone|ipad|ipod|mobile|tablet/i.test(ua);
  }

  function getIsDesktopLandingWidth() {
    if (typeof window === "undefined") return false;
    return window.innerWidth > 440 && !isTrueMobileDevice();
  }

  const [isDesktopLandingWidth, setIsDesktopLandingWidth] = useState(() =>
    getIsDesktopLandingWidth(),
  );

  useEffect(() => {
    const updateDesktopLandingWidth = () => {
      setIsDesktopLandingWidth(getIsDesktopLandingWidth());
    };

    updateDesktopLandingWidth();

    window.addEventListener("resize", updateDesktopLandingWidth);
    window.addEventListener("orientationchange", updateDesktopLandingWidth);

    return () => {
      window.removeEventListener("resize", updateDesktopLandingWidth);
      window.removeEventListener(
        "orientationchange",
        updateDesktopLandingWidth,
      );
    };
  }, []);

  // Smooth scroll to the very top (reusable)
  const scrollToTop = () => {
    requestAnimationFrame(() =>
      window.scrollTo({ top: 0, behavior: "smooth" }),
    );
  };

  // ── Search state derived from URL so refresh/back works
  const params = new URLSearchParams(location.search);
  const qParam = params.get("q") || "";
  const locParam = params.get("loc") || "";

  const isSearching = !!qParam.trim(); // used to hide hero/filters

  function setQueryParams(next) {
    const p = new URLSearchParams(location.search);
    if (next.q != null) next.q ? p.set("q", next.q) : p.delete("q");
    if (next.loc != null) next.loc ? p.set("loc", next.loc) : p.delete("loc");
    navigate({ search: p.toString() }, { replace: true });
  }

  // Filter and live-entry overlays
  const [sortOpen, setSortOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [showInsideEventSheet, setShowInsideEventSheet] = useState(false);

  useEffect(() => {
    onFilterOverlayChange?.(sortOpen || dateOpen);
  }, [sortOpen, dateOpen, onFilterOverlayChange]);

  useEffect(() => {
    return () => {
      onFilterOverlayChange?.(false);
    };
  }, [onFilterOverlayChange]);

  const userId = localStorage.getItem("userId");
  const accessToken = localStorage.getItem("accessToken");

  const contextValue = useContext(EventsContext) || {};
  const {
    selectedMarketId = "",
    selectedMarketLabel = "",
    isRestoringMarket = false,
    isLoadingEvents = false,
    eventsError = "",
    refreshEvents,
  } = contextValue;
  const marketEvents = contextValue.marketEvents;
  const [events, setEvents] = useState([]);
  const [eventsSource, setEventsSource] = useState(null);
  const eventsLoaded = Boolean(selectedMarketId) && !isLoadingEvents &&
    eventsSource === marketEvents;

  useEffect(() => {
    setEvents(
      selectedMarketId && Array.isArray(marketEvents)
        ? marketEvents
        : []
    );
    setEventsSource(marketEvents);
  }, [selectedMarketId, marketEvents]);

  const [currentUser, setCurrentUser] = useState(null);
  const [occupancies, setOccupancies] = useState({});
  const [filterType, setFilterType] = useState("all"); // all | live | upcoming | saved | registered

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filter = params.get("filter");
    const valid = ["all", "live", "upcoming", "saved", "registered"];

    if (filter && valid.includes(filter) && filter !== filterType) {
      setFilterType(filter);
    }
  }, [location.search, filterType]);

  // Date/time filter state
  const [selectedDateRange, setSelectedDateRange] = useState([
    { startDate: null, endDate: null, key: "selection", times: [] },
  ]);

  const safeRange = (
    Array.isArray(selectedDateRange) && selectedDateRange.length
      ? selectedDateRange
      : [{ startDate: null, endDate: null, key: "selection", times: [] }]
  ).map((item) => ({
    ...item,
    times: Array.isArray(item.times) ? item.times : [],
  }));

  // Sorting
  const [sortType, setSortType] = useState("relevance");

  useEffect(() => {
    if (sortType === "distAsc" || sortType === "distDesc") {
      setSortType("relevance");
    }
  }, [sortType]);

  // ——— Infinite scroll config ———
  const BATCH_SIZE = 4;
  const [batch, setBatch] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const loaderRef = useRef(null);

  // Detect when sticky engages
  const stickySentinelRef = useRef(null);
  const [stickyStuck, setStickyStuck] = useState(false);

  useEffect(() => {
    const sentinel = stickySentinelRef.current;
    if (!sentinel) return;

    const io = new IntersectionObserver(
      (entries) => setStickyStuck(!entries[0]?.isIntersecting),
      { root: null, threshold: 0 },
    );

    io.observe(sentinel);
    return () => io.disconnect();
  }, []);

  // Category pills (keep IDs in URL if you want)
  const [selectedCategories, setSelectedCategories] = useState(() =>
    (new URLSearchParams(location.search).get("types") || "")
      .split(",")
      .filter(Boolean),
  );
  useEffect(() => {
    const urlTypes = (new URLSearchParams(location.search).get("types") || "")
      .split(",")
      .filter(Boolean);
    const same =
      urlTypes.length === selectedCategories.length &&
      urlTypes.every((t, i) => t === selectedCategories[i]);
    if (!same) setSelectedCategories(urlTypes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // aliases
  const CAT_ALIAS = { arts: "art", women: "women-only" };
  const normCat = (id) => (CAT_ALIAS[id] || id || "").toLowerCase();

  const [insideEvent, setInsideEvent] = useState(null);
  const [showNotRegisteredSheet, setShowNotRegisteredSheet] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // For "focus" event logic
  const queryParams = new URLSearchParams(location.search);
  const focusEventId = queryParams.get("focusEventId") || null;
  function handleUnfocus() {
    const newParams = new URLSearchParams(location.search);
    newParams.delete("focusEventId");
    navigate({ search: newParams.toString() });
  }

  // track which events user is inside (to avoid repeating inside-event sheet)
  const shownInsideEventIds = useRef(new Set());

  const shownInsideSheetForEventRef = useRef(new Set());

  useEffect(() => {
    setShowInsideEventSheet(false);
    setInsideEvent(null);
    setShowNotRegisteredSheet(false);
    setSelectedEvent(null);
  }, [selectedMarketId]);

useEffect(() => {
  if (!userId || !eventsLoaded || eventsError) return;

  const checkPortalEntrySheets = () => {
    if (showInsideEventSheet) return;

    const now = Date.now();

    for (const ev of events) {
      const eventMarketId = ev.marketId?._id ?? ev.marketId;
      if (!selectedMarketId || String(eventMarketId || "") !== String(selectedMarketId)) continue;
      const eventKey =
        ev.mode === "mixer"
          ? `mixer-${ev._id}`
          : String(ev._id);

      if (
        shownInsideSheetForEventRef.current.has(
          eventKey
        )
      ) {
        continue;
      }

      const startMs = getEventStartMs(ev);
      const endMs = getEventEndMs(ev);

      if (!startMs || !endMs) continue;

      // Portal opens exactly 1 hour before start.
      const portalOpenMs =
        startMs - 60 * 60 * 1000;

      if (
        now < portalOpenMs ||
        now > endMs
      ) {
        continue;
      }

      const isRegistered =
        ev.attendeeIds?.some(
          (id) =>
            String(id) === String(userId)
        ) ||
        ev.attendees?.some(
          (a) =>
            String(a?._id ?? a) ===
            String(userId)
        );

      // Only registered guests get this popup.
      if (!isRegistered) continue;

      shownInsideSheetForEventRef.current.add(
        eventKey
      );

      setInsideEvent(ev);
      setShowInsideEventSheet(true);
      break;
    }
  };

  // Check immediately.
  checkPortalEntrySheets();

  // Also keep checking so the popup can appear
  // when the clock crosses the 1-hour mark,
  // even if the user has been sitting on this page.
  const intervalId = setInterval(
    checkPortalEntrySheets,
    15_000
  );

  return () =>
    clearInterval(intervalId);
}, [
  events,
  selectedMarketId,
  eventsLoaded,
  eventsError,
  userId,
  showInsideEventSheet,
]);

  const scrollPositionRef = useRef(0);

  // Closes focus if another overlay is opened
  useEffect(() => {
    if (focusEventId) {
      if (
        showInsideEventSheet ||
        dateOpen ||
        sortOpen
      ) {
        handleUnfocus();
      }
    }
  }, [
    focusEventId,
    showInsideEventSheet,
    dateOpen,
    sortOpen,
  ]);

  // Overlay freeze/unfreeze
  useEffect(() => {
    const insideOpen = showInsideEventSheet && !!userId;

    const overlayCount = insideOpen ? 1 : 0;

    console.log("[OverlayFreeze] flags:", {
      showInsideEventSheet,
      insideOpen,
      sortOpen,
      dateOpen,
      overlayCount,
    });

    // If an overlay is up, close sort/date sheets
    if (insideOpen && sortOpen) setSortOpen(false);
    if (insideOpen && dateOpen) setDateOpen(false);

    const themeMetaTag = document.querySelector('meta[name="theme-color"]');
    const alreadyFrozen = document.body.style.position === "fixed";

    if (overlayCount > 0 && !alreadyFrozen) {
      scrollPositionRef.current = window.scrollY || window.pageYOffset;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.width = "100%";

      document.body.classList.add("overlay-active");
      document.documentElement.classList.add("overlay-active");
      document.getElementById("root")?.classList.add("overlay-active");

      if (themeMetaTag) themeMetaTag.content = "#999998";
    } else if (overlayCount === 0 && alreadyFrozen) {
      document.body.classList.remove("overlay-active");
      document.documentElement.classList.remove("overlay-active");
      document.getElementById("root")?.classList.remove("overlay-active");

      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";

      window.scrollTo(0, scrollPositionRef.current);

      if (themeMetaTag) themeMetaTag.content = "#ffffff";
    }

    // ALWAYS restore on unmount so we don't strand the app in fixed position
    return () => {
      document.body.classList.remove("overlay-active");
      document.documentElement.classList.remove("overlay-active");
      document.getElementById("root")?.classList.remove("overlay-active");
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      const themeTag = document.querySelector('meta[name="theme-color"]');
      if (themeTag) themeTag.content = "#ffffff";
    };
  }, [showInsideEventSheet, sortOpen, dateOpen, userId]);

  // Reset infinite-scroll batch whenever filters/search/list size change
  useEffect(() => {
    setBatch(0);
  }, [
    filterType,
    selectedDateRange,
    sortType,
    qParam,
    locParam,
    selectedCategories,
    events.length,
    selectedMarketId,
  ]);

  const handleRegistration = React.useCallback(({ eventId, attendeeIds }) => {
    const count = attendeeIds.length;
    setEvents((prev) =>
      prev.map((ev) =>
        ev._id === eventId
          ? { ...ev, attendeeIds, attendees: attendeeIds, attendeeCount: count }
          : ev,
      ),
    );
  }, []);

  // ---------------------------------------------------
  //  📡 socket-io live updates (single connection)
  // ---------------------------------------------------
  useEffect(() => {
    if (!userId || !accessToken) return;

    // Stable handler refs so we can .off() properly
    const onEventUpdated = (updatedEvent) => {
      setEvents((prev) =>
        prev.map((ev) =>
          ev._id.toString() === updatedEvent._id.toString()
            ? { ...ev, ...updatedEvent }
            : ev,
        ),
      );
    };

    const onRegistrationUpdated = (data) => {
      handleRegistration(data);
    };

    const onSaveUpdated = ({ eventId, savedUsers, saveCount }) => {
      setEvents((prev) =>
        prev.map((ev) => {
          const evId = ev.mode === "mixer" ? `mixer-${ev._id}` : ev._id;
          return evId === eventId
            ? { ...ev, savedUserIds: savedUsers, saveCount }
            : ev;
        }),
      );
    };

    const onOccupancyUpdated = (updates) => {
      setOccupancies((prev) => {
        const next = { ...prev };
        if (Array.isArray(updates)) {
          updates.forEach(({ eventId, occupancyCount }) => {
            next[eventId] = occupancyCount;
          });
        } else if (updates && updates.eventId) {
          next[updates.eventId] = updates.occupancyCount;
        }
        return next;
      });
    };

    // IMPORTANT: include transports + withCredentials
    const socket = io(process.env.REACT_APP_API_URL, {
      auth: { token: accessToken },
      withCredentials: true,
      transports: ["websocket", "polling"], // or ['websocket'] if server is ws-only
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 300,
      reconnectionDelayMax: 3000,
    });

    console.log("[SOCKET] connecting");

    socket.on("connect", () => console.log("[SOCKET] connected", socket.id));
    socket.on("connect_error", (err) => console.error("[SOCKET] error", err));

    socket.on("eventUpdated", onEventUpdated);
    socket.on("registrationUpdated", onRegistrationUpdated);
    socket.on("saveUpdated", onSaveUpdated);
    socket.on("occupancyUpdated", onOccupancyUpdated);

    // On reconnect, refresh auth (if token rotated)
    socket.on("connect", () => {
      try {
        socket.auth = {
          token: localStorage.getItem("accessToken") || accessToken,
        };
      } catch {}
    });

    return () => {
      socket.off("eventUpdated", onEventUpdated);
      socket.off("registrationUpdated", onRegistrationUpdated);
      socket.off("saveUpdated", onSaveUpdated);
      socket.off("occupancyUpdated", onOccupancyUpdated);
      socket.disconnect();
    };
    // ⚠️ Depend only on values that must recreate the socket.
  }, [userId, accessToken, handleRegistration]);

  function handleNavigate(ev) {
    if (!userId) return;
    if (!ev.latitude || !ev.longitude) {
      alert("No location available.");
      return;
    }
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${ev.latitude},${ev.longitude}`;
    window.open(mapsUrl, "_blank");
  }

  function isUserRegistered(ev, uid) {
    if (!uid) return false;
    const uidStr = uid.toString ? uid.toString() : String(uid);

    if (Array.isArray(ev.attendeeIds) && ev.attendeeIds.length) {
      return ev.attendeeIds.some(
        (id) => (id && id.toString ? id.toString() : String(id)) === uidStr,
      );
    }
    if (Array.isArray(ev.attendees) && ev.attendees.length) {
      return ev.attendees.some(
        (a) => (a && a._id ? a._id.toString() : String(a)) === uidStr,
      );
    }
    return false;
  }

  const idEquals = (a, b) =>
    (a && a.toString ? a.toString() : String(a)) ===
    (b && b.toString ? b.toString() : String(b));

  async function handleSaveToggle(ev) {
    if (!userId) {
      navigate("/login");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setEvents((prev) =>
      prev.map((e) =>
        e._id === ev._id
          ? {
              ...e,
              savedUserIds: (e.savedUserIds || []).some((id) => idEquals(id, userId))
                ? (e.savedUserIds || []).filter((id) => !idEquals(id, userId))
                : [...(e.savedUserIds || []), userId],
            }
          : e,
      ),
    );

    const alreadySaved = (ev.savedUserIds || []).some((id) => idEquals(id, userId));
    const endpoint = alreadySaved ? "unsave" : "save";

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/events/${ev._id}/${endpoint}`,
        { userId },
      );
    } catch (err) {
      setEvents((prev) =>
        prev.map((e) =>
          e._id === ev._id
            ? {
                ...e,
                savedUserIds: alreadySaved
                  ? [...(e.savedUserIds || []), userId]
                  : (e.savedUserIds || []).filter((id) => !idEquals(id, userId)),
              }
            : e,
        ),
      );
      console.error("[Save toggle error]", err);
      alert("Failed to toggle save.");
    }
  }

  function handleSelectEvent(ev) {
    if (!userId) {
      navigate("/login");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const neutral = String(ev.mode || "").toLowerCase() === "neutral";
    const portalOpenMs = (getEventStartMs(ev) || 0) - 60 * 60 * 1000;
    if (isEventNotStarted(ev) && (!neutral || Date.now() < portalOpenMs)) {
      alert("This event has not started yet.");
      return;
    }
    if (isEventEnded(ev)) {
      alert("This event has ended.");
      return;
    }

    if (!isUserRegistered(ev, userId)) {
      setSelectedEvent(ev);
      setShowNotRegisteredSheet(true);
      return;
    }

    if (String(ev.mode || "").toLowerCase() === "neutral") {
      navigate(`/neutral/${ev._id}/intro`, { state: { event: ev } });
      return;
    }

    const isMixer = ev.mode === "mixer";
    const eventId = isMixer ? ev._id.replace(/^mixer-/, "") : ev._id;

    navigate(isMixer ? `/mixer/events/${eventId}` : "/intro", {
      state: { event: ev },
    });
  }

  // Filter out ended events
  const allEvents = selectedMarketId && eventsLoaded && !eventsError
    ? events.filter((ev) => {
        const eventMarketId = ev.marketId?._id ?? ev.marketId;
        return String(eventMarketId || "") === String(selectedMarketId);
      })
    : [];

  const filteredEvents = allEvents.filter((ev) => {
    if (String(ev.mode || "").toLowerCase() === "neutral") {
      const endMs = getEventEndMs(ev);
      return endMs && Date.now() < endMs;
    }
    return !isEventEnded(ev);
  });

  let eventsToDisplay = filteredEvents;

  if (filterType === "live") {
    eventsToDisplay = eventsToDisplay.filter(isEventOngoing);
  } else if (filterType === "upcoming") {
    eventsToDisplay = eventsToDisplay.filter(isEventNotStarted);
  } else if (filterType === "saved") {
    eventsToDisplay = eventsToDisplay.filter((ev) =>
      ev.savedUserIds?.some(
        (id) => (id.toString ? id.toString() : id) === userId,
      ),
    );
  } else if (filterType === "registered") {
    eventsToDisplay = eventsToDisplay.filter((ev) =>
      isUserRegistered(ev, userId),
    );
  }

  const { startDate, endDate, times } = selectedDateRange[0];
  // Any filter toggled? (query/location, date range, time-of-day, category chips, or top filterType)
  const filtersActive = React.useMemo(() => {
    const hasQuery = !!qParam.trim() || !!locParam.trim();
    const hasDate = Boolean(startDate) || Boolean(endDate);
    const hasTimes = Array.isArray(times) && times.length > 0;
    const hasCats =
      Array.isArray(selectedCategories) && selectedCategories.length > 0;
    const hasFilterType = filterType !== "all";
    const hasSort = sortType && sortType !== "relevance";

    return (
      hasQuery || hasDate || hasTimes || hasCats || hasFilterType || hasSort
    );
  }, [
    qParam,
    locParam,
    startDate,
    endDate,
    times,
    selectedCategories,
    filterType,
    sortType,
  ]);
  if (startDate || endDate) {
    eventsToDisplay = eventsToDisplay.filter((ev) => {
      const startMs = getEventStartMs(ev);
      if (!startMs) return false;

      const tz = ev.timeZone || "America/Los_Angeles";

      if (startDate) {
        const startBoundary = getEventStartMs({
          eventDate: getCalendarDate(startDate),
          startTime12: "12:00 AM",
          timeZone: tz,
        });
        if (startMs < startBoundary) return false;
      }

      if (endDate) {
        const endBoundary = getEventStartMs({
          eventDate: getCalendarDate(endDate),
          startTime12: "11:59 PM",
          timeZone: tz,
        });
        if (startMs > endBoundary) return false;
      }

      return true;
    });
  }

  if (Array.isArray(times) && times.length > 0) {
    eventsToDisplay = eventsToDisplay.filter((ev) => {
      const startMs = getEventStartMs(ev);
      const endMs = getEventEndMs(ev);
      const tz = ev.timeZone || "America/Los_Angeles";

      if (!startMs || !endMs) return false;

      const coveredSlots = getEventTimeSlots(startMs, endMs, tz);
      return times.some((userSlot) => coveredSlots.includes(userSlot));
    });
  }

  if (sortType !== "relevance") {
    eventsToDisplay = [...eventsToDisplay];

    eventsToDisplay.sort((a, b) => {
      switch (sortType) {
        case "dateAsc": {
          const aStart = getEventStartMs(a) || 0;
          const bStart = getEventStartMs(b) || 0;
          return aStart - bStart;
        }
        case "dateDesc": {
          const aStart = getEventStartMs(a) || 0;
          const bStart = getEventStartMs(b) || 0;
          return bStart - aStart;
        }

        case "nameAsc": {
          const aname = (a.name || "").toLowerCase();
          const bname = (b.name || "").toLowerCase();
          return aname.localeCompare(bname);
        }
        case "nameDesc": {
          const aname = (a.name || "").toLowerCase();
          const bname = (b.name || "").toLowerCase();
          return bname.localeCompare(aname);
        }
        default:
          return 0;
      }
    });
  }

  // ── Apply search filtering (must be before noEvents/totalPages/pageEvents)
  if (qParam || locParam) {
    const q = norm(qParam);
    const loc = norm(locParam);

    eventsToDisplay = eventsToDisplay.filter((ev) => {
      // text haystack (add/adjust fields to match your schema)
      const textHaystack = [
        ev.name,
        ev.title, // some backends use 'title'
        ev.description,
        ev.eventType,
        ev.mode,
        ev.hostedAt,
        Array.isArray(ev.tags) ? ev.tags.join(" ") : "",
      ]
        .map(norm)
        .join(" ");

      // location haystack
      const whereHaystack = [
        ev.city,
        ev.neighborhood,
        ev.locationName,
        ev.hostedAt,
        ev.region,
      ]
        .map(norm)
        .join(" ");

      const textOk = q ? textHaystack.includes(q) : true;
      const locOk = loc ? whereHaystack.includes(loc) : true;
      return textOk && locOk;
    });
  }
  // ── Apply CATEGORY pills filtering (AND logic across selected)
  // ── Apply CATEGORY pills filtering (OR logic across selected)
  if (selectedCategories.length > 0) {
    const wanted = new Set(selectedCategories.map(normCat));
    eventsToDisplay = eventsToDisplay.filter((ev) => {
      const tags = (ev.categoryIds || ev.categories || ev.tags || [])
        .map((t) => (t && t.id ? t.id : t))
        .map(normCat);

      // OR: keep event if it has at least one of the selected categories
      return tags.some((tag) => wanted.has(tag));
    });
  }

  /*
   * Under the default relevance order, keep the saved weekly
   * recommendation first. Because this runs after every filter,
   * the event is only pinned when it belongs in the current results.
   * A user-selected Sort By order always takes precedence.
   */
  if (
    sortType === "relevance" &&
    recommendedEvent?._id
  ) {
    const recommendedIndex =
      eventsToDisplay.findIndex(
        (event) =>
          String(event?._id) ===
          String(recommendedEvent._id)
      );

    if (recommendedIndex > 0) {
      eventsToDisplay = [
        eventsToDisplay[recommendedIndex],
        ...eventsToDisplay.slice(
          0,
          recommendedIndex
        ),
        ...eventsToDisplay.slice(
          recommendedIndex + 1
        ),
      ];
    }
  }

  const noEvents = eventsToDisplay.length === 0;
  const endIndex = Math.min((batch + 1) * BATCH_SIZE, eventsToDisplay.length);
  const pageEvents = eventsToDisplay.slice(0, endIndex);

  useEffect(() => {
    if (!loaderRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      const hit = entries[0]?.isIntersecting;
      const hasMore = (batch + 1) * BATCH_SIZE < eventsToDisplay.length;
      if (hit && hasMore && !loadingMore) {
        setLoadingMore(true);
        setTimeout(() => {
          setBatch((b) => b + 1);
          setLoadingMore(false);
        }, 300);
      }
    });
    obs.observe(loaderRef.current);
    return () => obs.disconnect();
  }, [loaderRef, batch, loadingMore, eventsToDisplay.length]);

  useEffect(() => {
    async function fetchCurrentUser() {
      if (!accessToken || !userId) return;

      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/users/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        setCurrentUser(data || null);
      } catch (err) {
        console.warn(
          "[current user fetch]",
          err?.response?.data || err.message,
        );
        setCurrentUser(null);
      }
    }

    fetchCurrentUser();
  }, [accessToken, userId]);




  useEffect(() => {
    if (!focusEventId) return;
    const elem = document.getElementById(`eventcard_${focusEventId}`);
    if (elem) elem.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusEventId, pageEvents.length]);

  function getEventCountText() {
    const count = eventsToDisplay.length;
    if (count === 0) return null;
    const plural = count === 1 ? "gathering" : "gatherings";

    if (filterType === "saved") {
      return (
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          You’ve saved&nbsp;<strong>{count}</strong>&nbsp;{plural}
        </span>
      );
    }
    if (filterType === "registered") {
      return (
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          You’re registered to&nbsp;<strong>{count}</strong>&nbsp;{plural}
        </span>
      );
    }
    if (filterType === "live") {
      return (
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          <strong>{count}</strong>&nbsp;live&nbsp;{plural}&nbsp;in {selectedMarketLabel || "your selected city"}
        </span>
      );
    }
    if (filterType === "upcoming") {
      return (
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          <strong>{count}</strong>&nbsp;upcoming&nbsp;{plural}&nbsp;in {selectedMarketLabel || "your selected city"}
        </span>
      );
    }
    return (
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        <strong>{count}</strong>&nbsp;{plural}&nbsp;in {selectedMarketLabel || "your selected city"}
      </span>
    );
  }

  const eventCountText = getEventCountText();

  const showReservationStatus =
    !filtersActive &&
    shouldShowReservationStatus(membership);

  if (!userId || isDesktopLandingWidth) {
    return (
      <>
        <style>
          {`
       @media (min-width: 441px) {
  .mobileLoggedOutFooter {
    display: none !important;
  }
}
        `}
        </style>

        <NonLoggedInEventsLanding
          events={eventsLoaded && !eventsError ? eventsToDisplay : []}
          selectedMarketId={selectedMarketId}
          selectedMarketLabel={selectedMarketLabel}
          isLoadingEvents={isRestoringMarket || isLoadingEvents || (Boolean(selectedMarketId) && !eventsLoaded && !eventsError)}
          eventsError={eventsError}
          onSeeGatherings={() => {
            const section = document.getElementById("gatherings");
            section?.scrollIntoView({ behavior: "smooth" });
          }}
          onEventClick={(event) => {
            navigate(`/events/${event._id || event.id}`);
          }}
        />

        <div className="mobileLoggedOutFooter">
          <MinimalFooter />
        </div>
      </>
    );
  }
  return (
    <>
      <div
        style={{
          background: `
            linear-gradient(
              to bottom,
              #fff 0%,
              #fff 25%,
              #fcfcfc 35%,
              #fcfcfc 65%,
              #fff 75%,
              #fff 100%
            )
          `,
          minHeight: "100vh",
        }}
      >
        <div style={{ position: "relative", minHeight: "90vh" }}>
          <div
            className="left-line"
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 25,
              width: 1,
              backgroundColor: "#eeee",
              zIndex: 1,
            }}
          />
          <div
            className="right-line"
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              right: 25,
              width: 1,
              backgroundColor: "#eeee",
              zIndex: 1,
            }}
          />

          <div
            className="events-page-container"
            style={{
              marginLeft: 60,
              marginRight: 60,
              position: "relative",
              zIndex: 1,
              paddingBottom: "0px",
              overflow: "visible", // ← ensure no ancestor blocks sticky
            }}
          >
            {focusEventId && (
              <div
                onClick={handleUnfocus}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  zIndex: 50,
                }}
              />
            )}
            {/* Sentinel: turns true when sticky header engages */}
            <div ref={stickySentinelRef} style={{ height: 1 }} />

            {/* ───────────────── Sticky search (FULL-BLEED + brand inline with search) ───────────────── */}
            <div className={`sticky-search ${stickyStuck ? "is-stuck" : ""}`}>
              <div className="sticky-search__bleed">
                <div className="sticky-search__inner">
                  <div className="sticky-search__content">
                    {/* ROW: logo (left) + search bar (right) */}
                    <div
                      className="sticky-search__row"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      {/* Logo shows only when sticky; clicking it scrolls to top */}
                      <button
                        type="button"
                        className="sticky-search__brand"
                        aria-label="Back to top"
                        onClick={() =>
                          window.scrollTo({ top: 0, behavior: "smooth" })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }
                        }}
                        style={{
                          // keep existing sizing logic
                          "--sticky-logo-w": "70px",
                          width: stickyStuck ? "var(--sticky-logo-w)" : 0,
                          opacity: stickyStuck ? 1 : 0,
                          overflow: "hidden",
                          transition:
                            "width 160ms ease, opacity 160ms ease, margin-right 160ms ease",
                          display: "flex",
                          alignItems: "center",
                          flex: "0 0 auto",
                          marginRight: stickyStuck ? 12 : 0,
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          cursor: stickyStuck ? "pointer" : "default",
                        }}
                      >
                        <TribeLogo className="sticky-search__logo" />
                      </button>
                      {/* Search bar */}
                      <div
                        className="sticky-search__field"
                        style={{ flex: 1, minWidth: 0 }}
                      >
                        <SearchBarWithSheet
                          defaultQuery={
                            new URLSearchParams(location.search).get("q") || ""
                          }
                          defaultLoc={
                            new URLSearchParams(location.search).get("loc") ||
                            ""
                          }
                          showClear
                          onSearch={(q, loc) => {
                            const p = new URLSearchParams(location.search);
                            q ? p.set("q", q) : p.delete("q");
                            loc ? p.set("loc", loc) : p.delete("loc");
                            navigate(
                              { search: p.toString() },
                              { replace: true },
                            );
                          }}
                          onClear={() => {
                            const p = new URLSearchParams(location.search);
                            p.delete("q");
                            p.delete("loc");
                            navigate(
                              { search: p.toString() },
                              { replace: true },
                            );
                          }}
                          onOpenSort={() => {
                            setDateOpen(false);
                            setSortOpen(true);
                          }}
                          onOpenCalendar={() => {
                            setSortOpen(false);
                            setDateOpen(true);
                          }}

                          isSortActive={sortType && sortType !== "relevance"}
                          isDateActive={
                            Boolean(selectedDateRange[0]?.startDate) ||
                            Boolean(selectedDateRange[0]?.endDate) ||
                            (Array.isArray(selectedDateRange[0]?.times) &&
                              selectedDateRange[0].times.length > 0)
                          }
                        />
                      </div>
                    </div>

                    {/* Pills under the search bar (UNCHANGED) */}
                    <div style={{ marginTop: 8 }}>
                      <CategoryPills
                        initialSelected={selectedCategories}
                        onChange={(ids) => {
                          setSelectedCategories(ids);
                          const p = new URLSearchParams(location.search);
                          ids.length
                            ? p.set("types", ids.join(","))
                            : p.delete("types");
                          navigate({ search: p.toString() }, { replace: true });
                          scrollToTop();
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {showReservationStatus && (
              <CardRequiredReservationBanner
                membership={membership}
                user={currentUser}
              />
            )}

            {/*
{!filtersActive && (
  <MembershipStatusBanner
    membership={membership}
    user={currentUser}
  />
)}
*/}

       <div
         style={{
           position: "relative",
           top: showReservationStatus ? 0 : -16,
           marginBottom: showReservationStatus ? 0 : -16,
         }}
       >
       <EventsFiltersAndHeadline
  userId={userId}
  filterType={filterType}
  setFilterType={(val) => {
    setFilterType(val);
    scrollToTop();
  }}
  selectedDateRange={selectedDateRange}
  setSelectedDateRange={setSelectedDateRange}
  sortType={sortType}
  setSortType={setSortType}
  sortOpen={sortOpen}
  setSortOpen={setSortOpen}
  dateOpen={dateOpen}
  setDateOpen={setDateOpen}
  filtersActive={filtersActive}
/>
       </div>

            {filtersActive && (
              <div style={resultsFoundStyle}>
                {eventsToDisplay.length} Gathering
                {eventsToDisplay.length === 1 ? "" : "s"} Found
              </div>
            )}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                margin: "0px 0 -4px 0",
                gap: 0,
              }}
            >
              {/* Left side: count chip OR "x results" */}
              {/*

              {!isSearching ? (
                eventCountText && (
                  <span
                    style={{
                      display       : 'inline-flex',
                      alignItems    : 'center',
                      padding       : '8px 12px',
                      background    : 'rgba(0,0,0,0.05)',
                      borderRadius  : 8,
                      fontSize      : '.8rem',
                      fontWeight    : 500,
                      color         : '#000',
                      fontFamily    : "'Open Sans', sans-serif",
                    }}
                  >
                    {eventCountText}
                  </span>
                )
              ) : (
                <span
                  style={{
                    display    : 'inline-flex',
                    alignItems : 'center',
                    fontSize   : '.9rem',
                    fontWeight : 600,
                    color      : '#0b0b0b',
                  }}
                >
                  <strong>{eventsToDisplay.length}</strong>&nbsp;result{eventsToDisplay.length === 1 ? '' : 's'}
                </span>
              )}

         {/*
{!isSearching && userId && (
  <span
    onClick={() => {
      setFilterType('saved');
      scrollToTop();
    }}
    style={{
      display         : 'inline-flex',
      marginLeft      : 'auto',
      alignItems      : 'center',
      gap             : 6,
      padding         : '8px 12px',
      borderRadius    : 8,
      backgroundColor : filterType === 'saved' ? '#007bff' : '#eee',
      color           : filterType === 'saved' ? '#fff'    : '#666',
      fontSize        : '.8rem',
      fontWeight      : 500,
      cursor          : 'pointer',
      userSelect      : 'none',
    }}
    title="Show saved events"
  >
    <FaRegHeart style={{ fontSize: 13 }} />
    Saved
  </span>
)}
*/}
            </div>

            <div
              id="events-list"
              style={{
                position: "relative",
                paddingTop: 0,
              }}
            >
              {isRestoringMarket || (selectedMarketId && !eventsLoaded && !eventsError) ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                    gap: 20,
                  }}
                >
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        height: 200,
                        backgroundColor: "#f0f0f0",
                        borderRadius: 8,
                        animation: "pulse 1.5s infinite ease-in-out",
                      }}
                    />
                  ))}
                  <style>
                    {`
                      @keyframes pulse {
                        0% { background-color: #f0f0f0; }
                        50% { background-color: #e0e0e0; }
                        100% { background-color: #f0f0f0; }
                      }
                    `}
                  </style>
                </div>
              ) : !selectedMarketId ? (
             <div style={ghostStateContainer}>
  <img
    src={SelectMarket}
    alt="Select your city"
    style={{
      ...ghostImageStyle,
      width: 162,
      height: 'auto',
    }}
  />
  <p style={ghostHeadingStyle}>Choose your city</p>
  <p style={ghostSubtitleStyle}>
    Select a city from the menu to see its gatherings.
  </p>
</div>
              ) : eventsError ? (
                <div style={ghostStateContainer}>
                  <p style={ghostHeadingStyle}>Couldn’t load gatherings</p>
                  <p style={ghostSubtitleStyle}>{eventsError}</p>
                  <button type="button" onClick={() => refreshEvents?.()} style={{ marginTop: 16 }}>
                    Try again
                  </button>
                </div>
              ) : noEvents ? (
                filterType === "saved" ? (
                  <div style={ghostStateContainer}>
                    <img
                      src={NoSaved}
                      alt="No saved events"
                      style={ghostImageStyle}
                    />
                    <p style={ghostHeadingStyle}>No Saved Events</p>
                    <p style={ghostSubtitleStyle}>
                      You haven’t saved any events yet. Tap the heart ♥ to add
                      one here.
                    </p>
                  </div>
                ) : filterType === "registered" ? (
                  <div style={ghostStateContainer}>
                    <img
                      src={NoEvent}
                      alt="No registered events"
                      style={ghostImageStyle}
                    />
                    <p style={ghostHeadingStyle}>No Registered Events</p>
                    <p style={ghostSubtitleStyle}>
                      You haven’t registered for any events yet.
                    </p>
                  </div>
                ) : (
                  <div style={ghostStateContainer}>
                    <img
                      src={NoEvent}
                      alt="No events"
                      style={ghostImageStyle}
                    />
                    <p style={ghostHeadingStyle}>No Gatherings Found</p>
                    <p style={ghostSubtitleStyle}>
                      Try adjusting your filters or come back later for more
                      options.
                    </p>
                  </div>
                )
              ) : (
                <>
                  <div
                    className="cardGrid"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(250px, 1fr))",
                      gap: 20,
                    }}
                  >
                    {pageEvents.map((ev) => {
                      const occupantCount =
                        occupancies[ev._id] !== undefined
                          ? occupancies[ev._id]
                          : ev.occupancyCount;
                      const live = isEventOngoing(ev);
                      const upcoming = isEventNotStarted(ev);

                      const isFocused = ev._id === focusEventId;

                      return (
                        <div key={ev._id}>
                          <EventCard
                            key={ev._id}
                            hasAccount={true}
                            userId={userId}
                            live={live}
                            upcoming={upcoming}
                            event={{
                              ...ev,
                              distanceMiles: null,
                              distanceKm: null,
                              distanceMeters: null,
                            }}
                            isRecommended={
  Boolean(recommendedEvent?._id) &&
  String(ev._id) ===
    String(recommendedEvent._id)
}
                            occupantCount={(() => {
                              const mixerKey = `mixer-${ev._id}`;
                              const normalKey = ev._id;

                              const mixerOccupancy = occupancies[mixerKey];
                              const normalOccupancy = occupancies[normalKey];

                              return mixerOccupancy !== undefined
                                ? mixerOccupancy
                                : normalOccupancy || ev.occupancyCount || 0;
                            })()}
                            isFocused={isFocused}
                            isUserSaved={ev.savedUserIds?.some(
                              (id) =>
                                (id.toString ? id.toString() : id) === userId,
                            )}
                            onSaveToggle={handleSaveToggle}
                            onNavigate={handleNavigate}
                            onSelectEvent={handleSelectEvent}
                            refreshEventData={(updated) =>
                              setEvents((prev) =>
                                prev.map((e) =>
                                  e._id === updated._id
                                    ? { ...e, ...updated }
                                    : e,
                                ),
                              )
                            }
                          />
                        </div>
                      );
                    })}
                  </div>

                  {/* Infinite scroll sentinel (Notifications-style spinner) */}
                  <div
                    ref={loaderRef}
                    aria-live="polite"
                    aria-busy={loadingMore ? "true" : "false"}
                    style={{
                      minHeight: loadingMore ? 56 : 0,
                      display: "flex",
                      justifyContent: "center",
                      padding: "12px 0",
                    }}
                  >
                    {loadingMore && <div style={notifSpinnerStyle} />}
                  </div>

                  {/* End-of-list indicator */}
                  {!loadingMore &&
                    pageEvents.length === eventsToDisplay.length &&
                    eventsToDisplay.length > 0 && (
                      <div style={{ margin: "10px 0 24px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            margin: "8px auto",
                            maxWidth: 480,
                            padding: "0 12px",
                          }}
                        >
                          <div
                            style={{
                              height: 1,
                              background: "#e5e7eb",
                              flex: "1 1 80px",
                            }}
                          />
                          <div
                            style={{
                              color: "#6b7280",
                              fontSize: ".85rem",
                              fontWeight: 600,
                              letterSpacing: ".02em",
                              whiteSpace: "nowrap",
                            }}
                          >
                            No more results
                          </div>
                          <div
                            style={{
                              height: 1,
                              background: "#e5e7eb",
                              flex: "1 1 80px",
                            }}
                          />
                        </div>

                        <div style={{ textAlign: "center", marginTop: 8 }}>
                          <button
                            type="button"
                            onClick={() =>
                              window.scrollTo({ top: 0, behavior: "smooth" })
                            }
                            style={{
                              border: "1px solid #d1d5db",
                              background: "#fff",
                              color: "#111827",
                              borderRadius: 999,
                              padding: "8px 14px",
                              fontSize: ".85rem",
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                            aria-label="Back to top"
                            title="Back to top"
                          >
                            Back to top
                          </button>
                        </div>
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
 /* ───────────────── sticky bar ───────────────── */
.sticky-search{
  position: sticky;
  top: 0;
  z-index: 40;
}

/* Full-bleed white background strip */
.sticky-search__bleed{
  background: #ffffff;            /* solid white */
  /* Cover left/right gutters too */
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  /* No blur anywhere */
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

/* Restore the page’s inner gutters for the contents */
.sticky-search__inner{
  padding-left: 60px;             /* match .events-page-container margins */
  padding-right: 60px;
  padding-top: 8px;
  padding-bottom: 10px;
}

/* NEW: a row that holds logo + search bar on the same line */
.sticky-row{
  display: flex;
  align-items: center;
  gap: 12px;
}

/* NEW: the brand slot sits to the left of the search bar */
.sticky-brand{
  display: flex;
  align-items: center;
  flex: 0 0 auto;                 /* don’t stretch */
}

/* Optional logo sizing (tweak if needed) */
.sticky-brand svg,
.sticky-brand img{
  width: 72px;
  height: auto;
}

@media (max-width: 768px) {
  .events-page-container {
    margin-left: 20px !important;
    margin-right: 20px !important;
  }
  .left-line, .right-line { display: none; }
  .sticky-search{ top: 0; }

  .sticky-search__inner{
    padding-left: 20px;           /* match mobile margins */
    padding-right: 20px;
    padding-top: 8px;
    padding-bottom: 10px;
  }

  .sticky-brand svg,
  .sticky-brand img{
    width: 100px;   /* match TopNav mobile */
    height: auto;
  }

}
  
/* SVG takes the same width as the wrapper variable */
.sticky-search__logo{
  width: var(--sticky-logo-w, 70px);  /* default 70px if var missing */
  height: auto;
  display: block;
}

/* (optional) ensure no other rule caps it on mobile */
@media (max-width: 768px){
  .sticky-brand svg,
  .sticky-brand img{
    width: var(--sticky-logo-w, 70px) !important;
    height: auto !important;
  }
}

/* ───────────────── truth serum (debug) ─────────────────
   Keep only while debugging; remove after. */
html, body, #root { overflow: visible !important; }
.events-page, .events-page-container {
  transform: none !important;
  filter: none !important;
  perspective: none !important;
  overflow: visible !important;
}

/* loader dots (smaller & smoother) */
@keyframes dotPulseFast {
  0%   { transform: scale(0.9);  opacity: .45; }
  50%  { transform: scale(1.03); opacity: 1;   }
  100% { transform: scale(0.9);  opacity: .45; }
}
.tri-dot{
  width: 6px;              /* smaller */
  height: 6px;             /* smaller */
  border-radius: 999px;
  background: #111;
  display: inline-block;
  animation: dotPulseFast .8s ease-in-out infinite; /* faster, smoother */
  transform-origin: center;
}
.tri-dot.sm{
  width: 6px;
  height: 6px;
}

@keyframes spin { 
  0% { transform: rotate(0deg); } 
  100% { transform: rotate(360deg); } 
}
        `}
      </style>

      <MinimalFooter />

      {showNotRegisteredSheet && selectedEvent && (
        <EventAlreadyStartedBottomSheet
          eventName={selectedEvent.name}
          onDismiss={() => setShowNotRegisteredSheet(false)}
        />
      )}
      {showInsideEventSheet &&
        insideEvent &&
        (() => {
          const requiresRegistration = (insideEvent?.capacity ?? 0) > 0;

          const isRegByAttendees =
            Array.isArray(insideEvent?.attendees) &&
            insideEvent.attendees.some(
              (a) =>
                (a && a._id ? a._id.toString() : String(a)) === String(userId),
            );

          const isRegByIds =
            Array.isArray(insideEvent?.attendeeIds) &&
            insideEvent.attendeeIds.some(
              (id) =>
                (id && id.toString ? id.toString() : String(id)) ===
                String(userId),
            );

          const isRegistered = Boolean(isRegByAttendees || isRegByIds);

          const startMs = getEventStartMs(insideEvent);

          const entrySheetEventStarted =
            !!startMs && Date.now() >= startMs;

          return (
            <EventLiveEntrySheet
              event={insideEvent}
              eventId={insideEvent._id}
              requiresRegistration={requiresRegistration}
              isRegistered={isRegistered}
              eventStarted={entrySheetEventStarted}
              galleryImages={insideEvent.galleryImages || []}
              eventName={insideEvent.name || "(x)"}
              hostName={insideEvent.hostedAt || "(x)"}
              onCancel={() => {
                setShowInsideEventSheet(false);
                setInsideEvent(null);
              }}
              // no onContinue — component handles mixer/standard routing internally
            />
          );
        })()}
    </>
  );
}

// Some local CSS style objects
const ghostStateContainer = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginTop: 40,
  marginBottom: 40,
  textAlign: "center",
};
const ghostImageStyle = {
  width: 180,
  maxWidth: "80%",
  marginBottom: 20,
};
const ghostHeadingStyle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: "0.35rem",
  letterSpacing: "-0.01em",
};

const ghostSubtitleStyle = {
  fontSize: ".88rem",
  color: "#6b7280",
  maxWidth: "240px",
  lineHeight: 1.45,
  margin: "0 auto",
};

const paginationBarStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginTop: 24,
  gap: 8,
};
const paginationBtnStyle = {
  border: "1px solid #ccc",
  backgroundColor: "#fff",
  color: "#333",
  padding: "6px 12px",
  cursor: "pointer",
  borderRadius: 4,
};
const paginationActiveStyle = {
  backgroundColor: "#000",
  color: "#fff",
  fontWeight: "bold",
  border: "1px solid #000",
};
// Same spinner style used on Notifications
const notifSpinnerStyle = {
  width: 18,
  height: 18,
  border: "3px solid #E7E9EE",
  borderTop: "3px solid #555",
  borderRadius: "50%",
  animation: "spin .8s linear infinite",
};
const resultsFoundStyle = {
  margin: "0px 0 20px",
  paddingLeft: 4,
  color: "rgba(0,0,0,0.72)",
  fontSize: ".9rem",
  lineHeight: 1.2,
  fontWeight: 700,
  letterSpacing: "-0.02em",
};
